import React, { useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { T, useTranslate } from '@tolgee/react';
import {
	SypacButton,
	SypacInput,
	SypacText,
} from '@sypac/component-library-react';
import Close from '../../../assets/Close';
import LoadingSpinner from '../../../assets/LoadingSpinner';
import {
	Contract,
	ContractsService,
	EditContractRequest,
} from '../../../services/contracts.service';
import { toastVariant } from '../../ToastVariant/toastVariant';
import PriceTag from '../../../assets/PriceTag';
import Tons from '../../../assets/Tons';
import { DatePicker } from '../../DatePicker/DatePicker';
import { NumericFormat } from 'react-number-format';
import { numberTwoDecimal } from '../../../utils/number.util';
import { cn } from '../../../lib/utils';
import { ContractStatus } from '../interfaces/Contract.interface';

type ModalEditContractProps = {
	isOpen: boolean;
	onClose: () => void;
	contract: Contract;
	onUpdated?: (updated: Contract) => void;
	mode?: 'edit' | 'resume';
};

const ModalEditContract: React.FC<ModalEditContractProps> = ({
	isOpen,
	onClose,
	contract,
	onUpdated,
	mode = 'edit',
}) => {
	const { t } = useTranslate();
	const isResumeMode = mode === 'resume';

	const toNum = (v: any) => Number(String(v ?? '').replace(',', '.')) || 0;

	const isEditingMarkup = useRef(false);
	const isEditingPrices = useRef(false);
	const lastChangedPrice = useRef<'seller' | 'buyer' | null>(null);

	const line = contract?.lines?.[0];

	const formatPriceString = (value: number) => {
		if (!Number.isFinite(value)) return '';
		const rounded = Math.round(value * 100) / 100;

		return rounded % 1 === 0
			? rounded.toString()
			: rounded.toFixed(2).replace(/\.?0+$/, '');
	};

	const getInitialBuyerPrice = () => {
		if (
			line?.pricePerTonSeller != null &&
			line?.markup != null &&
			line.pricePerTonSeller > 0
		) {
			const buyerWithMarkup = line.pricePerTonSeller * (1 + line.markup / 100);

			return formatPriceString(buyerWithMarkup);
		}

		return line?.pricePerTonBuyer?.toString() ?? '';
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			pricePerTonSeller: line?.pricePerTonSeller?.toString() ?? '',
			pricePerTonBuyer: getInitialBuyerPrice(),
			markup: line?.markup?.toString() ?? '',
			quantity: line?.contractQtyTons?.toString() ?? '',
			dateFrom: contract?.startsAt
				? dayjs(contract.startsAt).format('YYYY-MM-DD')
				: '',
			dateTo: contract?.expiresAt
				? dayjs(contract.expiresAt).format('YYYY-MM-DD')
				: '',
		},
		onSubmit: async (values, { setSubmitting }) => {
			try {
				const quantity = Number(values.quantity) || 0;
				const perTonSeller = Number(values.pricePerTonSeller) || 0;
				const markup = Number(values.markup) || 0;

				const payload: EditContractRequest & { status?: string } = {
					startsAt: dayjs(values.dateFrom).toISOString(),
					expiresAt: dayjs(values.dateTo).toISOString(),
					lines: [
						{
							lineId: line?.id,
							contractQtyTons: quantity,
							markup,
							pricePerTonSeller: perTonSeller,
							pricePerTonBuyer: perTonSeller,
						},
					],
				};

				if (isResumeMode && contract.status === ContractStatus.canceled) {
					payload.status =
						contract.orders.some((order) => order) && contract.ordersCount > 0
							? 'in_progress'
							: 'to_do';
				}

				const { data } = await ContractsService.editContract(
					contract.id,
					payload,
				);

				toastVariant(
					isResumeMode
						? t(
								'adminContracts.contractResumed',
								'Contract resumed successfully',
						  )
						: t(
								'adminContracts.contractUpdated',
								'Contract updated successfully',
						  ),
					false,
				);

				onUpdated?.(data);
				onClose();
			} catch (error) {
				toastVariant(
					t(
						'adminContracts.contractUpdateFailed',
						'Failed to update contract.',
					) + ` ${error?.toString?.() ?? ''}`,
					true,
				);
			} finally {
				setSubmitting(false);
			}
		},
		validationSchema: Yup.object().shape({
			pricePerTonSeller: Yup.number()
				.typeError(
					t('adminContracts.priceMustBeNumber', 'Price must be a number'),
				)
				.required(
					t(
						'adminContracts.priceRequired',
						'Please enter seller price per ton',
					),
				)
				.positive(
					t(
						'adminContracts.priceGreaterThanZero',
						'Price must be greater than 0',
					),
				),
			pricePerTonBuyer: Yup.number()
				.typeError(
					t('adminContracts.priceMustBeNumber', 'Price must be a number'),
				)
				.required(
					t('adminContracts.priceRequired', 'Please enter buyer price per ton'),
				)
				.positive(
					t(
						'adminContracts.priceGreaterThanZero',
						'Price must be greater than 0',
					),
				),
			markup: Yup.number()
				.typeError(
					t('adminContracts.markupMustBeNumber', 'Mark-up must be a number'),
				)
				.required(t('adminContracts.markupRequired', 'Please enter mark-up')),
			quantity: Yup.number()
				.typeError(
					t('adminContracts.quantityMustBeNumber', 'Quantity must be a number'),
				)
				.required(t('adminContracts.quantityRequired', 'Please enter quantity'))
				.positive(
					t(
						'adminContracts.quantityGreaterThanZero',
						'Quantity must be greater than 0',
					),
				),
			dateFrom: Yup.string()
				.required(
					t('adminContracts.dateFromRequired', 'Please select start date'),
				)
				.test(
					'date-from-not-before-minimum',
					t(
						'adminContracts.dateFromNotBeforeMinimum',
						'Start date cannot be earlier than the minimum allowed date',
					),
					function (val) {
						if (!val) return true;

						const chosen = dayjs(val).startOf('day');
						const todayDate = dayjs().startOf('day');
						const originalStartDate = contract?.startsAt
							? dayjs(contract.startsAt).startOf('day')
							: todayDate;

						const minDate = originalStartDate.isBefore(todayDate)
							? originalStartDate
							: todayDate;

						return chosen.diff(minDate) >= 0;
					},
				),
			dateTo: Yup.string()
				.required(t('adminContracts.dateToRequired', 'Please select end date'))
				.test(
					'date-to-after-from',
					t(
						'adminContracts.dateToAfterFrom',
						'End date must be the same as or after start date',
					),
					function (val) {
						const { dateFrom } = this.parent as { dateFrom?: string };
						return (
							!!val &&
							!!dateFrom &&
							dayjs(val).isValid() &&
							dayjs(dateFrom).isValid() &&
							dayjs(val).startOf('day').diff(dayjs(dateFrom).startOf('day')) >=
								0
						);
					},
				),
		}),
		validateOnMount: true,
	});

	const perTonBuyer = toNum(formik.values.pricePerTonBuyer);
	const perTonSeller = toNum(formik.values.pricePerTonSeller);
	const qty = toNum(formik.values.quantity);

	const canCalculateProfitLoss = qty > 0 && perTonSeller > 0 && perTonBuyer > 0;

	const profitDifference = (perTonBuyer - perTonSeller) * qty;
	const isLoss = profitDifference < 0;
	const profitOrLoss = Math.abs(profitDifference);

	const formattedProfit = canCalculateProfitLoss
		? numberTwoDecimal(profitOrLoss)
		: null;

	const showProfitDash =
		!canCalculateProfitLoss ||
		!formattedProfit ||
		Number(formattedProfit) === 0;

	const currencyCode =
		contract?.countryCode === 'MDA'
			? 'MDL'
			: contract?.countryCode === 'POL'
			? 'PLN'
			: 'PLN';

	useEffect(() => {
		const seller = toNum(formik.values.pricePerTonSeller);
		const buyer = toNum(formik.values.pricePerTonBuyer);
		const markupRaw = String(formik.values.markup ?? '').trim();
		const hasMarkup = markupRaw !== '' && markupRaw !== '-';

		if (!hasMarkup && seller > 0 && buyer > 0) {
			const timeoutId = setTimeout(() => {
				const newMarkup = ((buyer - seller) / seller) * 100;
				formik.setFieldValue('markup', formatPriceString(newMarkup), false);
			}, 500);

			return () => clearTimeout(timeoutId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formik.values.pricePerTonSeller,
		formik.values.pricePerTonBuyer,
		formik.values.markup,
	]);

	const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		isEditingPrices.current = true;
		isEditingMarkup.current = false;

		const { name, value } = e.target;
		const sellerName = 'pricePerTonSeller';
		const buyerName = 'pricePerTonBuyer';

		lastChangedPrice.current = name === sellerName ? 'seller' : 'buyer';

		const currentSeller =
			name === sellerName
				? toNum(value)
				: toNum(formik.values.pricePerTonSeller);
		const currentBuyer =
			name === buyerName ? toNum(value) : toNum(formik.values.pricePerTonBuyer);

		const markupRaw = String(formik.values.markup ?? '').trim();
		const hasMarkup = markupRaw !== '' && markupRaw !== '-';
		const markupValue = hasMarkup ? toNum(markupRaw) : NaN;

		formik.handleChange(e);

		if (name === sellerName) {
			if (hasMarkup && Number.isFinite(markupValue) && currentSeller > 0) {
				const newBuyer = currentSeller * (1 + markupValue / 100);
				formik.setFieldValue(buyerName, formatPriceString(newBuyer), false);
			}
		} else if (name === buyerName) {
			if (hasMarkup && Number.isFinite(markupValue) && currentBuyer > 0) {
				const denom = 1 + markupValue / 100;
				if (denom !== 0) {
					const newSeller = currentBuyer / denom;
					formik.setFieldValue(sellerName, formatPriceString(newSeller), false);
				}
			}
		}

		setTimeout(() => {
			formik.setFieldTouched(name, true, true);
			isEditingPrices.current = false;
		}, 0);
	};

	const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		isEditingMarkup.current = true;
		isEditingPrices.current = false;

		const { value } = e.target;
		const markupRaw = value.trim();
		const hasMarkup = markupRaw !== '' && markupRaw !== '-';
		const markupValue = hasMarkup ? toNum(markupRaw) : NaN;

		const seller = toNum(formik.values.pricePerTonSeller);
		const buyer = toNum(formik.values.pricePerTonBuyer);

		formik.handleChange(e);

		if (!hasMarkup || !Number.isFinite(markupValue)) {
			setTimeout(() => {
				isEditingMarkup.current = false;
			}, 0);
			return;
		}

		if (seller > 0 && buyer === 0) {
			const newBuyer = seller * (1 + markupValue / 100);
			formik.setFieldValue(
				'pricePerTonBuyer',
				formatPriceString(newBuyer),
				false,
			);
		} else if (buyer > 0 && seller === 0) {
			const denom = 1 + markupValue / 100;
			if (denom !== 0) {
				const newSeller = buyer / denom;
				formik.setFieldValue(
					'pricePerTonSeller',
					formatPriceString(newSeller),
					false,
				);
			}
		} else if (seller > 0 && buyer > 0) {
			if (lastChangedPrice.current === 'seller') {
				const newBuyer = seller * (1 + markupValue / 100);
				formik.setFieldValue(
					'pricePerTonBuyer',
					formatPriceString(newBuyer),
					false,
				);
			} else if (lastChangedPrice.current === 'buyer') {
				const denom = 1 + markupValue / 100;
				if (denom !== 0) {
					const newSeller = buyer / denom;
					formik.setFieldValue(
						'pricePerTonSeller',
						formatPriceString(newSeller),
						false,
					);
				}
			} else {
				const newBuyer = seller * (1 + markupValue / 100);
				formik.setFieldValue(
					'pricePerTonBuyer',
					formatPriceString(newBuyer),
					false,
				);
			}
		}

		setTimeout(() => {
			isEditingMarkup.current = false;
		}, 0);
	};

	const minDateFrom = useMemo(() => {
		const today = dayjs().format('YYYY-MM-DD');
		const originalStartDate = contract?.startsAt
			? dayjs(contract.startsAt).format('YYYY-MM-DD')
			: today;

		return dayjs(originalStartDate).isBefore(dayjs(today))
			? originalStartDate
			: today;
	}, [contract?.startsAt]);

	return isOpen ? (
		<div className="modal-overlay" onClick={onClose}>
			<div
				className="w-[828px] modal-inside outline-none box-border"
				onClick={(e) => e.stopPropagation()}
			>
				<form onSubmit={formik.handleSubmit} className="flex flex-col">
					<div className="flex items-center justify-between p-3">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-90">
								{isResumeMode ? (
									<T keyName="modalSelfPickup.resumeContract">
										Resume contract
									</T>
								) : (
									<T keyName="adminContracts.editContract">Edit contract</T>
								)}
							</p>
						</SypacText>
						<SypacButton variant="subTitle">
							<button
								type="button"
								className="flex justify-center items-center h-[32px] w-[32px] p-0 bg-white border border-solid border-gray-10 transition hover:border-gray-60 rounded-lg"
								onClick={onClose}
							>
								<Close width="10" height="10" />
							</button>
						</SypacButton>
					</div>
					<div className="border-0 border-solid border-t-[1px] border-gray-10" />

					<div className="flex flex-col gap-3 p-3">
						{isResumeMode && (
							<div className="flex flex-col gap-3">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px]">
										<T keyName="adminContracts.selectPriceQuantity">
											Select price & quantity
										</T>
									</p>
								</SypacText>
								<div className="h-fit flex justify-center bg-[#EBECF2] border border-solid border-gray-10 rounded-10 p-[10px]">
									<SypacInput
										error={
											!!(formik.touched.quantity && formik.errors.quantity)
										}
										className="relative w-full"
									>
										<SypacText className="mb-2" variant="overline-normal-large">
											<p>
												<T keyName="adminContracts.productQuantity">
													Product quantity
												</T>{' '}
												<span className="text-red">*</span>
											</p>
										</SypacText>
										<input
											className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
												formik.touched.quantity && formik.errors.quantity
													? 'border-red-orange'
													: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
											}`}
											name="quantity"
											type="text"
											inputMode="decimal"
											pattern="[0-9]*[.,]?[0-9]*"
											placeholder={t(
												'adminContracts.enterQuantity',
												'Enter quantity',
											)}
											value={formik?.values.quantity}
											onChange={formik.handleChange}
										/>
										<div className="absolute top-[36px] left-[10px]">
											<Tons color="#A2A2A2" />
										</div>
										<div className="absolute top-[24px] right-[12px]">
											<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
												t
											</p>
										</div>
										{formik?.touched.quantity && formik?.errors.quantity ? (
											<span className="input-error">
												{formik.errors.quantity}
											</span>
										) : null}
									</SypacInput>
								</div>
							</div>
						)}

						<div className="flex gap-3">
							<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
								<SypacInput
									error={
										!!(
											formik.touched.pricePerTonSeller &&
											formik.errors.pricePerTonSeller
										)
									}
									className="relative w-full"
								>
									<SypacText className="mb-2" variant="overline-normal-large">
										<p>
											<T keyName="adminContracts.pricePerTonSeller">
												Price per ton (Seller)
											</T>{' '}
											<span className="text-red">*</span>
										</p>
									</SypacText>
									<input
										className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
											formik.touched.pricePerTonSeller &&
											formik.errors.pricePerTonSeller
												? 'border-red-orange'
												: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
										}`}
										name="pricePerTonSeller"
										type="text"
										inputMode="decimal"
										pattern="[0-9]*[.,]?[0-9]*"
										placeholder={t(
											'adminContracts.enterProductPriceSeller',
											'Enter seller price',
										)}
										value={formik?.values.pricePerTonSeller}
										onChange={handlePriceChange}
									/>
									<div className="absolute top-[36px] left-[10px]">
										<PriceTag color="#A2A2A2" />
									</div>
									<div className="absolute top-[24px] right-[12px]">
										<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
											PLN
										</p>
									</div>
									{formik?.touched.pricePerTonSeller &&
									formik?.errors.pricePerTonSeller ? (
										<span className="input-error">
											{formik.errors.pricePerTonSeller}
										</span>
									) : null}
								</SypacInput>
							</div>

							<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
								<SypacInput
									error={
										!!(
											formik.touched.pricePerTonBuyer &&
											formik.errors.pricePerTonBuyer
										)
									}
									className="relative w-full"
								>
									<SypacText className="mb-2" variant="overline-normal-large">
										<p>
											<T keyName="adminContracts.pricePerTonBuyer">
												Price per ton (Buyer)
											</T>{' '}
											<span className="text-red">*</span>
										</p>
									</SypacText>
									<input
										className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
											formik.touched.pricePerTonBuyer &&
											formik.errors.pricePerTonBuyer
												? 'border-red-orange'
												: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
										}`}
										name="pricePerTonBuyer"
										type="text"
										inputMode="decimal"
										pattern="[0-9]*[.,]?[0-9]*"
										placeholder={t(
											'adminContracts.enterProductPriceBuyer',
											'Enter buyer price',
										)}
										value={formik?.values.pricePerTonBuyer}
										onChange={handlePriceChange}
									/>
									<div className="absolute top-[36px] left-[10px]">
										<PriceTag color="#A2A2A2" />
									</div>
									<div className="absolute top-[24px] right-[12px]">
										<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
											PLN
										</p>
									</div>
									{formik?.touched.pricePerTonBuyer &&
									formik?.errors.pricePerTonBuyer ? (
										<span className="input-error">
											{formik.errors.pricePerTonBuyer}
										</span>
									) : null}
								</SypacInput>
							</div>
						</div>

						{!isResumeMode ? (
							<div className="flex gap-3">
								<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
									<SypacInput
										error={!!(formik.touched.markup && formik.errors.markup)}
										className="relative w-full"
									>
										<SypacText className="mb-2" variant="overline-normal-large">
											<p>
												<T keyName="adminContracts.markUp">Mark-up</T>{' '}
												<span className="text-red">*</span>
											</p>
										</SypacText>
										<input
											className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
												formik.touched.markup && formik.errors.markup
													? 'border-red-orange'
													: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
											}`}
											name="markup"
											type="text"
											inputMode="decimal"
											pattern="-?[0-9]*[.,]?[0-9]*"
											placeholder={t(
												'adminContracts.enterMarkUp',
												'Enter mark-up',
											)}
											value={formik?.values.markup}
											onChange={handleMarkupChange}
										/>
										<div className="absolute top-[36px] left-[10px]">
											<PriceTag color="#A2A2A2" />
										</div>
										<div className="absolute top-[24px] right-[12px]">
											<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
												%
											</p>
										</div>
										{formik?.touched.markup && formik?.errors.markup ? (
											<span className="input-error">
												{formik.errors.markup}
											</span>
										) : null}
									</SypacInput>
								</div>

								<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
									<SypacInput
										error={
											!!(formik.touched.quantity && formik.errors.quantity)
										}
										className="relative w-full"
									>
										<SypacText className="mb-2" variant="overline-normal-large">
											<p>
												<T keyName="adminContracts.productQuantity">
													Product quantity
												</T>{' '}
												<span className="text-red">*</span>
											</p>
										</SypacText>
										<input
											className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
												formik.touched.quantity && formik.errors.quantity
													? 'border-red-orange'
													: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
											}`}
											name="quantity"
											type="text"
											inputMode="decimal"
											pattern="[0-9]*[.,]?[0-9]*"
											placeholder={t(
												'adminContracts.enterQuantity',
												'Enter quantity',
											)}
											value={formik?.values.quantity}
											onChange={formik.handleChange}
										/>
										<div className="absolute top-[36px] left-[10px]">
											<Tons color="#A2A2A2" />
										</div>
										<div className="absolute top-[24px] right-[12px]">
											<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
												t
											</p>
										</div>
										{formik?.touched.quantity && formik?.errors.quantity ? (
											<span className="input-error">
												{formik.errors.quantity}
											</span>
										) : null}
									</SypacInput>
								</div>
							</div>
						) : (
							<div className="flex gap-3">
								<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
									<SypacInput
										error={!!(formik.touched.markup && formik.errors.markup)}
										className="relative w-full"
									>
										<SypacText className="mb-2" variant="overline-normal-large">
											<p>
												<T keyName="adminContracts.markUp">Mark-up</T>{' '}
												<span className="text-red">*</span>
											</p>
										</SypacText>
										<input
											className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
												formik.touched.markup && formik.errors.markup
													? 'border-red-orange'
													: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
											}`}
											name="markup"
											type="text"
											inputMode="decimal"
											pattern="-?[0-9]*[.,]?[0-9]*"
											placeholder={t(
												'adminContracts.enterMarkUp',
												'Enter mark-up',
											)}
											value={formik?.values.markup}
											onChange={handleMarkupChange}
										/>
										<div className="absolute top-[36px] left-[10px]">
											<PriceTag color="#A2A2A2" />
										</div>
										<div className="absolute top-[24px] right-[12px]">
											<p className="text-gray-40 text-[14px] leading-[16px] tracking-[-0.01em]">
												%
											</p>
										</div>
										{formik?.touched.markup && formik?.errors.markup ? (
											<span className="input-error">
												{formik.errors.markup}
											</span>
										) : null}
									</SypacInput>
								</div>

								<div
									className={cn(
										'w-full h-fit flex flex-col gap-[10px] items-center justify-center border border-solid rounded-10 px-[10px] py-[19px]',
										canCalculateProfitLoss && isLoss
											? 'bg-[#F1CACE]/20 border-[#F2A6AA]'
											: 'bg-alabaster border-gray-10/50',
									)}
								>
									<SypacText variant="overline-normal-large">
										<p>
											<T
												keyName={
													isLoss
														? 'adminContracts.loss'
														: 'adminContracts.profit'
												}
											>
												{isLoss ? 'Loss' : 'Profit'}
											</T>
										</p>
									</SypacText>

									<SypacText variant="overline-normal-large">
										<p className="text-[16px] leading-[24px]">
											{showProfitDash ? (
												'—'
											) : (
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													decimalScale={2}
													value={formattedProfit}
													suffix={` ${currencyCode}`}
													prefix={isLoss ? '-' : ''}
												/>
											)}
										</p>
									</SypacText>
								</div>
							</div>
						)}

						<div className="border-0 border-solid border-t-[1px] border-gray-10" />

						<div className="flex flex-col gap-3">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 text-[14px] leading-[16px]">
									<T keyName="adminContracts.contractValidity">
										Contract validity
									</T>
								</p>
							</SypacText>
							<div className="flex gap-3">
								<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
									<SypacInput
										error={
											!!(formik.touched.dateFrom && formik.errors.dateFrom)
										}
										className="w-full"
									>
										<SypacText className="mb-2" variant="overline-normal-large">
											<p>
												<T keyName="adminContracts.dateFrom">Date from</T>{' '}
												<span className="text-red">*</span>
											</p>
										</SypacText>

										<DatePicker
											min={minDateFrom}
											max={
												formik?.values.dateTo
													? dayjs(formik.values.dateTo).format('YYYY-MM-DD')
													: undefined
											}
											value={formik?.values.dateFrom?.toString?.() || ''}
											onChange={(date) =>
												formik.setFieldValue('dateFrom', date)
											}
											placeholder={t(
												'adminContracts.selectDate',
												'Select date',
											)}
											allowOtherMonthSelection
											error={
												!!(formik.touched.dateFrom && formik.errors.dateFrom)
											}
										/>
										{formik.touched.dateFrom && formik.errors.dateFrom ? (
											<span className="text-xs text-red-orange mt-[2px]">
												{formik.errors.dateFrom}
											</span>
										) : null}
									</SypacInput>
								</div>

								<div className="w-full h-fit flex justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 p-[10px]">
									<SypacInput
										error={!!(formik.touched.dateTo && formik.errors.dateTo)}
										className="w-full"
									>
										<SypacText className="mb-2" variant="overline-normal-large">
											<p>
												<T keyName="adminContracts.dateTo">Date to</T>{' '}
												<span className="text-red">*</span>
											</p>
										</SypacText>

										<DatePicker
											min={
												(formik?.values.dateFrom &&
													String(formik.values.dateFrom)) ||
												dayjs().format('YYYY-MM-DD')
											}
											value={formik?.values.dateTo?.toString?.() || ''}
											onChange={(date) => formik.setFieldValue('dateTo', date)}
											placeholder={t(
												'adminContracts.selectDate',
												'Select date',
											)}
											allowOtherMonthSelection
											error={!!(formik.touched.dateTo && formik.errors.dateTo)}
										/>
										{formik.touched.dateTo && formik.errors.dateTo ? (
											<span className="text-xs text-red-orange mt-[2px]">
												{formik.errors.dateTo}
											</span>
										) : null}
									</SypacInput>
								</div>
							</div>
						</div>
					</div>

					<div className="flex w-full py-2.5 xl-2xl:py-3 gap-3 [&_button]:transition border border-solid border-b-0 rounded-10 border-gray-10 -mx-[1px] mt-auto">
						<SypacButton
							variant="secondary"
							size="small"
							className="w-full pl-2.5 xl-2xl:pl-3"
						>
							<button
								type="reset"
								className="w-full py-2.5 rounded-lg"
								onClick={onClose}
							>
								<SypacText variant="body-regular-medium">
									<p>
										<T keyName="modalChangeRequest.cancel">Cancel</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
						<SypacButton
							variant="primary"
							size="small"
							className="w-full pr-2.5 xl-2xl:pr-3"
						>
							<button
								type="submit"
								className={`w-full py-2.75 rounded-lg ${
									formik.isSubmitting
										? 'bg-primary-violet/80 text-gray-500'
										: ''
								}`}
								disabled={formik.isSubmitting}
							>
								<SypacText variant="body-regular-medium">
									<p className="flex text-white gap-3">
										{mode === 'resume' ? (
											<T keyName="modalEditContract.resumeContract">
												Resume contract
											</T>
										) : (
											<T keyName="modalEditContract.confirm">Confirm</T>
										)}
										{formik.isSubmitting && <LoadingSpinner />}
									</p>
								</SypacText>
							</button>
						</SypacButton>
					</div>
				</form>
			</div>
		</div>
	) : null;
};

export default ModalEditContract;
